// contexts/TrialContext.js
import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { auth, db } from "../firebase";
import { doc, onSnapshot } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";

const TrialContext = createContext();

function deriveState(data) {
  const planValue = data?.plan?.toLowerCase() || "free";
  const subStatus = data?.subscriptionStatus || null;
  const isProStatus = planValue === "pro" || planValue === "pro_plus";
  const isProPlusStatus = planValue === "pro_plus";

  const trialStartedAt = data?.trialStartedAt?.toDate?.();
  let daysLeft = 14;
  if (trialStartedAt) {
    const daysUsed = Math.floor(
      (Date.now() - trialStartedAt.getTime()) / (1000 * 60 * 60 * 24)
    );
    daysLeft = Math.max(0, 14 - daysUsed);
  }

  return {
    plan: planValue,
    subscriptionStatus: subStatus,
    isPro: isProStatus,
    isProPlus: isProPlusStatus,
    trialDaysLeft: daysLeft,
    isTrialExpired: daysLeft <= 0 && !isProStatus,
  };
}

export function TrialProvider({ children }) {
  const [plan, setPlan] = useState("free");
  const [isPro, setIsPro] = useState(false);
  const [isProPlus, setIsProPlus] = useState(false);
  const [isTrialExpired, setIsTrialExpired] = useState(false);
  const [trialDaysLeft, setTrialDaysLeft] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  // Keep a ref to the Firestore listener so we can unsub when user changes
  const docUnsubRef = useRef(null);

  useEffect(() => {
    const authUnsub = onAuthStateChanged(auth, (user) => {
      // Tear down any previous Firestore listener
      if (docUnsubRef.current) {
        docUnsubRef.current();
        docUnsubRef.current = null;
      }

      if (!user) {
        setPlan("free");
        setIsPro(false);
        setIsProPlus(false);
        setIsTrialExpired(false);
        setTrialDaysLeft(null);
        setSubscriptionStatus(null);
        setLoading(false);
        return;
      }

      // Real-time listener — updates immediately whenever Firestore changes
      // (e.g. Stripe webhook sets subscriptionStatus: "active" after payment retry succeeds)
      const ref = doc(db, "users", user.uid);
      docUnsubRef.current = onSnapshot(
        ref,
        (snap) => {
          const derived = deriveState(snap.data());
          setPlan(derived.plan);
          setSubscriptionStatus(derived.subscriptionStatus);
          setIsPro(derived.isPro);
          setIsProPlus(derived.isProPlus);
          setTrialDaysLeft(derived.trialDaysLeft);
          setIsTrialExpired(derived.isTrialExpired);
          setLoading(false);
        },
        (err) => {
          console.error("⚠️ TrialContext snapshot error", err);
          setLoading(false);
        }
      );
    });

    return () => {
      authUnsub();
      if (docUnsubRef.current) docUnsubRef.current();
    };
  }, []);

  return (
    <TrialContext.Provider
      value={{
        plan,
        setPlan,
        isPro,
        setIsPro,
        isProPlus,
        setIsProPlus,
        isTrialExpired,
        setIsTrialExpired,
        trialDaysLeft,
        subscriptionStatus,
        setSubscriptionStatus,
        loading,
      }}
    >
      {children}
    </TrialContext.Provider>
  );
}

export function useTrial() {
  return useContext(TrialContext);
}
